import {
  BasicProfile,
  BearingType,
  bearingTypes,
  GeneratableBearing,
  HeelSettings,
  OVERHANG_CUT_TYPES,
  OverhangCutType,
  Profile,
  ProfileJoint,
  ProfileLine,
  ProfileLineType,
} from '@paragon/contract-library';

import { calculateProfileLineGeometry, createProfileLine } from '../../library/profiles/profileManipulation';
import { ToolError } from './types';

/** Positions closer than this, in inches, are the same point. */
const TOLERANCE = 1 / 32;

const LINE_TYPES: readonly ProfileLineType[] = ['TopChord', 'BottomChord', 'Heel', 'SeatCut'];

export interface OutlinePoint {
  readonly x: number;
  readonly y: number;
  /** The line from this point to the next one; the last point's line closes the outline back to the first. */
  readonly line: ProfileLineType;
}

export interface BearingInput {
  /** The bearing’s left edge, in inches from the truss’s left end. */
  readonly x: number;
  readonly width: number;
  readonly type?: BearingType;
  /** Which chord bears on it; a top chord bearing can sit under an overhang, as at a tail bearing. */
  readonly chord?: 'Top' | 'Bottom';
}

/** A truss’s whole shape as a drawing gives it, in inches, with x from the left end and y up from the bottom. */
export interface ProfileGeometry {
  readonly outline: readonly OutlinePoint[];
  readonly leftOverhang?: number;
  readonly rightOverhang?: number;
  readonly leftOverhangCut?: OverhangCutType;
  readonly rightOverhangCut?: OverhangCutType;
  readonly bearings?: readonly BearingInput[];
  /** Where webs must meet the top chord. With these, the top chord gets no other panel points. */
  readonly topChordPanelPoints?: readonly number[];
  /** Where webs must meet the bottom chord. With these, the bottom chord gets no other panel points. */
  readonly bottomChordPanelPoints?: readonly number[];
}

export const PROFILE_GEOMETRY_SCHEMA = {
  outline: {
    type: 'array',
    description:
      'The outline’s corners, clockwise from the bottom-left: up the left end, along the top chord, down the right ' +
      'end, and back along the bottom chord. x is from the left end and y up from the bottom, so the lowest-left ' +
      'corner is (0, 0). `line` is the kind of line from this corner to the next; the last one closes the outline. ' +
      'Overhangs are not part of the outline.',
    items: {
      type: 'object',
      properties: {
        x: { type: 'number' },
        y: { type: 'number' },
        line: { type: 'string', enum: LINE_TYPES },
      },
      required: ['x', 'y', 'line'],
    },
  },
  leftOverhang: { type: 'number', description: 'Horizontal length of the top chord past the left end.' },
  rightOverhang: { type: 'number', description: 'Horizontal length of the top chord past the right end.' },
  leftOverhangCut: {
    type: 'string',
    enum: OVERHANG_CUT_TYPES,
    description:
      'How the left overhang’s end is cut. Horizontal is a seat cut, flat on a bearing under the overhang, as at a ' +
      'tail bearing; Plumb is vertical; Square is square to the chord.',
  },
  rightOverhangCut: { type: 'string', enum: OVERHANG_CUT_TYPES, description: 'As leftOverhangCut, on the right.' },
  bearings: {
    type: 'array',
    description:
      'Bearings, replacing the truss’s own. Any not at an end make a cantilever. A bearing under the top chord ' +
      'past the bottom chord’s end, as at a tail bearing, is a Top chord bearing under an overhang: its x is ' +
      'negative on a left overhang.',
    items: {
      type: 'object',
      properties: {
        x: { type: 'number', description: 'The bearing’s left edge, from the truss’s left end.' },
        width: { type: 'number' },
        type: { type: 'string', enum: bearingTypes, description: 'Defaults to DoublePlate.' },
        chord: { type: 'string', enum: ['Bottom', 'Top'], description: 'The chord it bears. Defaults to Bottom.' },
      },
      required: ['x', 'width'],
    },
  },
  topChordPanelPoints: {
    type: 'array',
    items: { type: 'number' },
    description:
      'x of each top chord panel point between the outline’s corners, from the drawing’s dimensions. When given, ' +
      'these are the only top chord panel points, so the webs land exactly there.',
  },
  bottomChordPanelPoints: {
    type: 'array',
    items: { type: 'number' },
    description: 'As topChordPanelPoints, for the bottom chord.',
  },
} as const;

function readNumber(value: unknown, what: string) {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new ToolError(`${what} must be a number.`);
  return value;
}

function readNumbers(value: unknown, what: string) {
  if (value == null) return undefined;
  if (!Array.isArray(value)) throw new ToolError(`${what} must be a list of numbers.`);
  return value.map((item, index) => readNumber(item, `${what}[${index}]`));
}

/** Checks the geometry part of a tool’s input and returns it typed. */
export function readProfileGeometry(input: Record<string, unknown>): ProfileGeometry {
  const outline = input.outline;
  if (!Array.isArray(outline) || outline.length < 3) throw new ToolError('"outline" must list at least three corners.');

  return {
    outline: outline.map((point: Record<string, unknown>, index) => {
      if (point == null || typeof point !== 'object') throw new ToolError(`outline[${index}] must be an object.`);
      if (!LINE_TYPES.includes(point.line as ProfileLineType))
        throw new ToolError(`outline[${index}].line must be one of ${LINE_TYPES.join(', ')}.`);
      return {
        x: readNumber(point.x, `outline[${index}].x`),
        y: readNumber(point.y, `outline[${index}].y`),
        line: point.line as ProfileLineType,
      };
    }),
    leftOverhang: input.leftOverhang == null ? undefined : readNumber(input.leftOverhang, '"leftOverhang"'),
    rightOverhang: input.rightOverhang == null ? undefined : readNumber(input.rightOverhang, '"rightOverhang"'),
    leftOverhangCut: readOverhangCut(input.leftOverhangCut, '"leftOverhangCut"'),
    rightOverhangCut: readOverhangCut(input.rightOverhangCut, '"rightOverhangCut"'),
    bearings:
      input.bearings == null
        ? undefined
        : (input.bearings as Record<string, unknown>[]).map((bearing, index) => {
            if (bearing == null || typeof bearing !== 'object')
              throw new ToolError(`bearings[${index}] must be an object.`);
            if (bearing.type != null && !bearingTypes.includes(bearing.type as BearingType))
              throw new ToolError(`bearings[${index}].type must be one of ${bearingTypes.join(', ')}.`);
            if (bearing.chord != null && bearing.chord !== 'Top' && bearing.chord !== 'Bottom')
              throw new ToolError(`bearings[${index}].chord must be Top or Bottom.`);
            return {
              x: readNumber(bearing.x, `bearings[${index}].x`),
              width: readNumber(bearing.width, `bearings[${index}].width`),
              type: bearing.type as BearingType | undefined,
              chord: bearing.chord as 'Top' | 'Bottom' | undefined,
            };
          }),
    topChordPanelPoints: readNumbers(input.topChordPanelPoints, '"topChordPanelPoints"'),
    bottomChordPanelPoints: readNumbers(input.bottomChordPanelPoints, '"bottomChordPanelPoints"'),
  };
}

function signedArea(points: readonly { x: number; y: number }[]) {
  let area = 0;
  points.forEach((point, index) => {
    const next = points[(index + 1) % points.length];
    area += point.x * next.y - next.x * point.y;
  });
  return area / 2;
}

/** The height of the vertical line at one end of the outline, or 0 if that end comes to a point. */
function heelHeightAt(outline: readonly OutlinePoint[], x: number) {
  const ys = outline.filter((point) => Math.abs(point.x - x) < TOLERANCE).map((point) => point.y);
  return ys.length < 2 ? 0 : Math.max(...ys) - Math.min(...ys);
}

function withHeelHeight(settings: HeelSettings, heelHeight: number): HeelSettings {
  const { interiorButtCut, chordTypeRunThrough } = settings;
  return { heelMethod: 'HeelHeight', interiorButtCut, chordTypeRunThrough, heelHeight };
}

function readOverhangCut(value: unknown, name: string): OverhangCutType | undefined {
  if (value == null) return undefined;
  if (!OVERHANG_CUT_TYPES.includes(value as OverhangCutType))
    throw new ToolError(`${name} must be one of ${OVERHANG_CUT_TYPES.join(', ')}.`);
  return value as OverhangCutType;
}

/**
 * Makes `xs` the only panel points on a profile’s top or bottom chord lines, so webs meet the chord exactly there.
 * Points at a chord line’s ends are already panel points.
 */
export function setChordPanelPoints<T extends BasicProfile>(
  profile: T,
  lineType: 'TopChord' | 'BottomChord',
  xs: readonly number[],
): T {
  const jointsByGuid = new Map(profile.profileJoints.map((joint) => [joint.guid, joint]));
  const used = new Set<number>();
  const lineEnds: number[] = [];

  const profileLines = profile.profileLines.map((line) => {
    if (line.profileLineType !== lineType) return line;
    const baseJoint = jointsByGuid.get(line.baseJointGuid);
    const endJoint = jointsByGuid.get(line.endJointGuid);
    if (baseJoint == null || endJoint == null) return line;
    lineEnds.push(baseJoint.location.x, endJoint.location.x);
    const low = Math.min(baseJoint.location.x, endJoint.location.x);
    const high = Math.max(baseJoint.location.x, endJoint.location.x);
    if (high - low < TOLERANCE) return line;

    const fixedJoints = xs
      .filter((x) => x > low + TOLERANCE && x < high - TOLERANCE)
      .map((x) => {
        used.add(x);
        return { guid: crypto.randomUUID(), x };
      });
    const rebuilt = createProfileLine(lineType, baseJoint, endJoint, fixedJoints);
    return {
      ...rebuilt,
      guid: line.guid,
      // The given panel points are the only ones: no floating joints between them.
      webJointSegments: rebuilt.webJointSegments.map((segment) => ({ ...segment, numberOfFloatingJoints: 0 })),
    };
  });

  for (const x of xs) {
    if (!used.has(x) && !lineEnds.some((end) => Math.abs(end - x) < TOLERANCE))
      throw new ToolError(`No ${lineType === 'TopChord' ? 'top' : 'bottom'} chord line runs over x = ${x}.`);
  }
  return { ...profile, profileLines };
}

/**
 * Builds a profile from a drawing’s geometry, keeping everything else (heel and overhang cuts, bevels, plies, name)
 * from `base`.
 */
export function buildProfileFromGeometry(base: Profile, geometry: ProfileGeometry): Profile {
  const { outline } = geometry;
  const xs = outline.map((point) => point.x);
  const ys = outline.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  if (Math.abs(minX) > TOLERANCE || Math.abs(Math.min(...ys)) > TOLERANCE)
    throw new ToolError('The outline must start at x = 0 on the left and y = 0 at the bottom.');
  if (maxX - minX < 1) throw new ToolError('The outline has no length.');

  outline.forEach((point, index) => {
    const next = outline[(index + 1) % outline.length];
    if (Math.hypot(next.x - point.x, next.y - point.y) < TOLERANCE)
      throw new ToolError(`outline[${index}] and the next corner are the same point.`);
  });
  if (signedArea(outline) > 0)
    throw new ToolError(
      'List the outline clockwise: up the left end, along the top chord, then back along the bottom.',
    );

  const joints: ProfileJoint[] = outline.map((point) => ({
    guid: crypto.randomUUID(),
    location: { x: point.x, y: point.y },
  }));

  const outlineLines: ProfileLine[] = outline.map((point, index) =>
    createProfileLine(point.line, joints[index], joints[(index + 1) % joints.length]),
  );
  let withPanelPoints: BasicProfile = { ...base, profileJoints: joints, profileLines: outlineLines };
  if (geometry.topChordPanelPoints != null)
    withPanelPoints = setChordPanelPoints(withPanelPoints, 'TopChord', geometry.topChordPanelPoints);
  if (geometry.bottomChordPanelPoints != null)
    withPanelPoints = setChordPanelPoints(withPanelPoints, 'BottomChord', geometry.bottomChordPanelPoints);
  const { profileLines } = withPanelPoints;

  const bearings: readonly GeneratableBearing[] =
    geometry.bearings?.map((bearing) => ({
      generatableBearingType: 'HorizontalGeneratableBearing',
      guid: crypto.randomUUID(),
      bearingType: bearing.type ?? 'DoublePlate',
      chordLocation: bearing.chord ?? 'Bottom',
      offset: bearing.x,
      width: bearing.width,
      anchor: 'Left',
      isContinuous: false,
      offsetSide: 'Left',
      connectors: [],
    })) ?? base.generatableBearings;

  const leftOverhang = geometry.leftOverhang ?? base.leftOverhang.distance;
  const rightOverhang = geometry.rightOverhang ?? base.rightOverhang.distance;
  for (const bearing of geometry.bearings ?? []) {
    if (bearing.width <= 0) throw new ToolError('A bearing’s width must be more than 0.');
    // Only the top chord reaches out over the overhangs.
    const [left, right] = bearing.chord === 'Top' ? [minX - leftOverhang, maxX + rightOverhang] : [minX, maxX];
    if (bearing.x < left - TOLERANCE || bearing.x + bearing.width > right + TOLERANCE)
      throw new ToolError(
        `The bearing at x = ${bearing.x} runs past the ${bearing.chord === 'Top' ? 'top' : 'bottom'} chord’s ends.`,
      );
  }

  return {
    ...base,
    profileJoints: joints,
    profileLines,
    leftOverhang: {
      ...base.leftOverhang,
      distance: leftOverhang,
      overhangCutType: geometry.leftOverhangCut ?? base.leftOverhang.overhangCutType,
    },
    rightOverhang: {
      ...base.rightOverhang,
      distance: rightOverhang,
      overhangCutType: geometry.rightOverhangCut ?? base.rightOverhang.overhangCutType,
    },
    leftHeelSettings: withHeelHeight(base.leftHeelSettings, heelHeightAt(outline, minX)),
    rightHeelSettings: withHeelHeight(base.rightHeelSettings, heelHeightAt(outline, maxX)),
    generatableBearings: bearings,
  };
}

const round = (value: number) => Math.round(value * 16) / 16;

/** A profile’s geometry in the same terms `buildProfileFromGeometry` takes, so the model can check its work. */
export function describeProfileGeometry(profile: Profile) {
  const lines = profile.profileLines.map((line) => ({ line, geometry: calculateProfileLineGeometry(profile, line) }));
  const panelPoints = (lineType: ProfileLineType) =>
    [
      ...new Set(
        profile.profileLines
          .filter((line) => line.profileLineType === lineType)
          .flatMap((line) => [
            ...line.fixedJoints.map((joint) => joint.x),
            ...line.webJointSegments.flatMap((segment) => segment.floatingJoints.map((joint) => joint.x)),
          ])
          .map(round),
      ),
    ].sort((a, b) => a - b);

  // Steep lines a little off vertical are usually a misread drawing, so call them out for the model to check.
  const nearlyVertical = lines.flatMap(({ line, geometry }) => {
    const run = Math.abs(geometry.endPoint.x - geometry.basePoint.x);
    const rise = Math.abs(geometry.endPoint.y - geometry.basePoint.y);
    return run > TOLERANCE && rise > 3 * run
      ? [
          {
            line: line.profileLineType,
            from: { x: round(geometry.basePoint.x), y: round(geometry.basePoint.y) },
            to: { x: round(geometry.endPoint.x), y: round(geometry.endPoint.y) },
            offVertical: round(run),
          },
        ]
      : [];
  });

  const xs = profile.profileJoints.map((joint) => joint.location.x);
  return {
    ...(nearlyVertical.length > 0 ? { nearlyVertical } : {}),
    overallLength: round(Math.max(...xs) - Math.min(...xs)),
    outline: lines.map(({ line, geometry }) => ({
      x: round(geometry.basePoint.x),
      y: round(geometry.basePoint.y),
      line: line.profileLineType,
    })),
    leftOverhang: round(profile.leftOverhang.distance),
    rightOverhang: round(profile.rightOverhang.distance),
    leftOverhangCut: profile.leftOverhang.overhangCutType,
    rightOverhangCut: profile.rightOverhang.overhangCutType,
    heelHeights: {
      left: 'heelHeight' in profile.leftHeelSettings ? round(profile.leftHeelSettings.heelHeight) : null,
      right: 'heelHeight' in profile.rightHeelSettings ? round(profile.rightHeelSettings.heelHeight) : null,
    },
    topChordSlopes: lines
      .filter(({ line }) => line.profileLineType === 'TopChord')
      .map(({ geometry }) => {
        const run = geometry.endPoint.x - geometry.basePoint.x;
        return Math.abs(run) < TOLERANCE
          ? null
          : Math.round(((geometry.endPoint.y - geometry.basePoint.y) / run) * 12 * 1000) / 1000;
      }),
    bearings: profile.generatableBearings.map((bearing) => ({
      offset: round(bearing.offset),
      from: bearing.offsetSide,
      width: round(bearing.width),
      chord: bearing.chordLocation,
      type: bearing.bearingType,
    })),
    topChordPanelPoints: panelPoints('TopChord'),
    bottomChordPanelPoints: panelPoints('BottomChord'),
  };
}
